/**
 * AI assist on the /review correction box. Two distinct intents that the UI must
 * keep visually separate:
 *  - "draft":  the box is empty but a model answer exists → offer to draft one.
 *  - "polish": the box has the reviewer's text → offer to polish it (the result
 *              is previewed and adopted explicitly, never overwritten in place).
 *  - "disabled": nothing to work from (empty box, no model answer).
 */
export type AiAssistMode = "draft" | "polish" | "disabled";

/**
 * Decide what the AI assist control should do, from the current correction box
 * (`after`) and the model's original answer (`before`). Whitespace-only counts as
 * empty so a box of spaces never looks like a real draft.
 */
export function aiAssistMode(after: string, before: string): AiAssistMode {
  if (after.trim().length > 0) return "polish";
  if (before.trim().length > 0) return "draft";
  return "disabled";
}

/** One span of a two-column diff: shared text, a deletion, or an insertion. */
export type DiffOpType = "equal" | "del" | "ins";
export interface DiffOp {
  type: DiffOpType;
  text: string;
}

/**
 * Above this token count per side the LCS diff would allocate an unbounded table,
 * so we fall back to a single del+ins block. Keeps the Uint16Array bounded
 * (≈ 1201 × 1201 × 2 bytes ≈ 2.9 MB worst case). Named constant, not magic.
 */
export const MAX_DIFF_TOKENS = 1200;

/**
 * Split a string into diff tokens: each CJK character on its own, runs of
 * latin/digits together, runs of whitespace together, and any other single
 * character on its own. This gives readable mixed Chinese/English diffs without a
 * heavyweight word segmenter.
 */
export function tokenizeForDiff(s: string): string[] {
  const re = /[A-Za-z0-9]+|\s+|[\s\S]/gu;
  return s.match(re) ?? [];
}

/** Merge consecutive ops of the same type so the rendered diff has fewer nodes. */
function mergeOps(ops: DiffOp[]): DiffOp[] {
  const out: DiffOp[] = [];
  for (const op of ops) {
    const last = out[out.length - 1];
    if (last !== undefined && last.type === op.type) last.text += op.text;
    else out.push({ ...op });
  }
  return out;
}

/**
 * Token-level diff of `original` → `rewritten` via a longest-common-subsequence
 * backtrack. Returns merged ops; the left preview column renders `equal`+`del`,
 * the right renders `equal`+`ins`. Reconstruct invariant: joining equal+del text
 * equals `original`, joining equal+ins text equals `rewritten`.
 */
export function diffTokens(original: string, rewritten: string): DiffOp[] {
  const a = tokenizeForDiff(original);
  const b = tokenizeForDiff(rewritten);

  if (a.length > MAX_DIFF_TOKENS || b.length > MAX_DIFF_TOKENS) {
    const ops: DiffOp[] = [];
    if (original.length > 0) ops.push({ type: "del", text: original });
    if (rewritten.length > 0) ops.push({ type: "ins", text: rewritten });
    return ops;
  }

  const n = a.length;
  const m = b.length;
  const w = m + 1;
  // dp[i*w + j] = LCS length of a[i:] and b[j:]. Uint16 is safe: lengths ≤ 1200.
  const dp = new Uint16Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * w + j] =
        a[i] === b[j]
          ? dp[(i + 1) * w + (j + 1)]! + 1
          : Math.max(dp[(i + 1) * w + j]!, dp[i * w + (j + 1)]!);
    }
  }

  const raw: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      raw.push({ type: "equal", text: a[i]! });
      i++;
      j++;
    } else if (dp[(i + 1) * w + j]! >= dp[i * w + (j + 1)]!) {
      raw.push({ type: "del", text: a[i]! });
      i++;
    } else {
      raw.push({ type: "ins", text: b[j]! });
      j++;
    }
  }
  while (i < n) raw.push({ type: "del", text: a[i++]! });
  while (j < m) raw.push({ type: "ins", text: b[j++]! });

  return mergeOps(raw);
}
